import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  ReceivingState,
  UndocumentedItem,
  DocumentedItem,
} from './receiving.types'

export const useReceivingStore = create<ReceivingState>()(
  devtools(
    (set, get) => ({
      activeStep: 0,
      ownershipType: null,
      productType: null,
      undocumentedItems: [],
      documentedItems: [],
      selectedLocationId: null,
      isNavigationBlocked: false,
      modal: { isOpen: false, data: {} },

      setActiveStep: (step: number) => set({ activeStep: step }),
      setOwnershipType: (type) => set({ ownershipType: type }),
      setProductType: (type) => set({ productType: type }),
      setUndocumentedItems: (items) => set({ undocumentedItems: items }),
      setDocumentedItems: (items) => set({ documentedItems: items }),
      setSelectedLocationId: (locationId) =>
        set({ selectedLocationId: locationId }),
      setIsNavigationBlocked: (blocked) =>
        set({ isNavigationBlocked: blocked }),
      setModal: (modal) => set({ modal }),

      addUndocumentedItem: (
        item: Omit<UndocumentedItem, 'id' | 'scannedAt'>
      ) => {
        const { undocumentedItems } = get()

        const existingItemIndex = undocumentedItems.findIndex(
          (existingItem: UndocumentedItem) =>
            existingItem.udi === item.udi && existingItem.udi
        )

        if (existingItemIndex !== -1) {
          set({
            undocumentedItems: undocumentedItems.map((existingItem, index) =>
              index === existingItemIndex
                ? {
                    ...existingItem,
                    quantity: existingItem.quantity + (item.quantity || 1),
                  }
                : existingItem
            ),
          })
        } else {
          const newItem = {
            ...item,
            id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            scannedAt: new Date(),
            quantity: item.quantity || 1,
          } as UndocumentedItem
          set({ undocumentedItems: [...undocumentedItems, newItem] })
        }
      },

      updateItemQuantity: (itemId: string, quantity: number) => {
        set((state) => ({
          undocumentedItems: state.undocumentedItems.map((item) =>
            item.id === itemId
              ? { ...item, quantity: Math.max(1, quantity) }
              : item
          ),
        }))
      },

      moveToDocumented: (items: UndocumentedItem[]) => {
        const documentedItems: DocumentedItem[] = items.map(
          (item: UndocumentedItem) => ({
            ...item,
            documentedAt: new Date(),
            reviewedBy: 'current_user',
          })
        )
        set((state) => ({
          documentedItems: [...state.documentedItems, ...documentedItems],
          undocumentedItems: state.undocumentedItems.filter(
            (item: UndocumentedItem) =>
              !items.some((docItem: UndocumentedItem) => docItem.id === item.id)
          ),
        }))
      },

      resetWorkflow: () => {
        set({
          activeStep: 0,
          ownershipType: null,
          productType: null,
          undocumentedItems: [],
          documentedItems: [],
          selectedLocationId: null,
          isNavigationBlocked: false,
          modal: { isOpen: false, data: {} },
        })
      },

      hasUnsavedChanges: () => {
        const {
          ownershipType,
          productType,
          undocumentedItems,
          documentedItems,
        } = get()
        return (
          ownershipType !== null ||
          productType !== null ||
          undocumentedItems.length > 0 ||
          documentedItems.length > 0
        )
      },
    }),
    { name: 'receiving-store' }
  )
)
